<div class="audit-dashboard">

  <!-- Page header -->
  <div class="dashboard-header">
    <h1 class="dashboard-title">Audit Log</h1>
    <p class="dashboard-subtitle">Internal activity across users, projects, sites, proposals, and documents</p>
  </div>

  <!-- Filter bar -->
  <div class="filter-card">
    <div class="filter-bar">

      <div class="filter-field filter-field--wide">
        <label class="filter-label">Search</label>
        <mat-form-field appearance="outline" class="filter-mat-field">
          <input matInput [(ngModel)]="filterSearch" (ngModelChange)="onFilterChange()" placeholder="UUID, Project ID/Name, or Order ID" />
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>
        <span class="filter-hint">Auto-detects UUID, Project ID (2607-00676), or Order ID (2606-00616-001-ESA)</span>
      </div>

      <div class="filter-field filter-field--state">
        <label class="filter-label">State</label>
        <mat-form-field appearance="outline" class="filter-mat-field">
          <mat-select [(ngModel)]="filterState" (ngModelChange)="onDropdownChange()">
            <mat-option value="">All</mat-option>
            <mat-option *ngFor="let s of stateOptions" [value]="s.code">{{ s.name }}</mat-option>
          </mat-select>
        </mat-form-field>
        <span class="filter-hint">Matches user &amp; site locations only</span>
      </div>

      <div class="filter-field filter-field--location">
        <label class="filter-label">Location</label>
        <mat-form-field appearance="outline" class="filter-mat-field">
          <input matInput [(ngModel)]="filterLocation" (ngModelChange)="onFilterChange()" placeholder="City, zip, or address" />
        </mat-form-field>
        <span class="filter-hint">Matches user &amp; site locations only</span>
      </div>

      <div class="filter-field">
        <label class="filter-label">Entity type</label>
        <mat-form-field appearance="outline" class="filter-mat-field">
          <mat-select [(ngModel)]="filterEntityType" (ngModelChange)="onDropdownChange()">
            <mat-option value="">All</mat-option>
            <mat-option *ngFor="let t of entityTypes" [value]="t">{{ t | titlecase }}</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="filter-field">
        <label class="filter-label">Action</label>
        <mat-form-field appearance="outline" class="filter-mat-field">
          <mat-select [(ngModel)]="filterAction" (ngModelChange)="onDropdownChange()">
            <mat-option value="">All</mat-option>
            <mat-option *ngFor="let a of actions" [value]="a">{{ a | titlecase }}</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="filter-field">
        <label class="filter-label">User</label>
        <mat-form-field appearance="outline" class="filter-mat-field">
          <input matInput [(ngModel)]="filterUser" (ngModelChange)="onFilterChange()" placeholder="Any user" />
        </mat-form-field>
      </div>

      <div class="filter-field filter-field--date">
        <label class="filter-label">From</label>
        <mat-form-field appearance="outline" class="filter-mat-field">
          <input matInput [matDatepicker]="pickerFrom" [(ngModel)]="filterDateFrom" (ngModelChange)="onDropdownChange()" placeholder="mm/dd/yyyy" />
          <mat-datepicker-toggle matSuffix [for]="pickerFrom"></mat-datepicker-toggle>
          <mat-datepicker #pickerFrom></mat-datepicker>
        </mat-form-field>
      </div>

      <div class="filter-field filter-field--date">
        <label class="filter-label">To</label>
        <mat-form-field appearance="outline" class="filter-mat-field">
          <input matInput [matDatepicker]="pickerTo" [(ngModel)]="filterDateTo" (ngModelChange)="onDropdownChange()" placeholder="mm/dd/yyyy" />
          <mat-datepicker-toggle matSuffix [for]="pickerTo"></mat-datepicker-toggle>
          <mat-datepicker #pickerTo></mat-datepicker>
        </mat-form-field>
      </div>

      <div class="filter-actions">
        <button class="btn-clear" *ngIf="hasActiveFilters" (click)="clearFilters()">
          <mat-icon class="clear-icon">filter_alt_off</mat-icon>
          Clear filters
        </button>

        <button class="btn-export" (click)="onExportCsv()">
          <mat-icon class="export-icon">download</mat-icon>
          Export CSV
        </button>
      </div>

    </div>
  </div>

  <!-- Loading spinner -->
  <div class="spinner-container" *ngIf="loading">
    <mat-spinner diameter="40"></mat-spinner>
  </div>

  <!-- Error state (load) -->
  <div class="state-card" *ngIf="!loading && error">
    <mat-icon class="state-icon state-icon--error">error_outline</mat-icon>
    <p>Failed to load audit log. Please try again.</p>
  </div>

  <!-- Export error state -->
  <div class="state-card state-card--export-error" *ngIf="exportError">
    <mat-icon class="state-icon state-icon--error">error_outline</mat-icon>
    <p>CSV export failed. Please try again.</p>
  </div>

  <!-- Empty state -->
  <div class="state-card" *ngIf="!loading && !error && rows.length === 0">
    <mat-icon class="state-icon">search_off</mat-icon>
    <p>No audit records match your filters.</p>
  </div>

  <!-- Data table -->
  <div class="table-panel" *ngIf="!loading && !error && rows.length > 0">
    <div class="table-container">
      <table mat-table [dataSource]="rows" class="audit-table">

        <!-- When -->
        <ng-container matColumnDef="timestamp">
          <th mat-header-cell *matHeaderCellDef>When</th>
          <td mat-cell *matCellDef="let row" class="cell-when">{{ row.timestamp | date:'medium' }}</td>
        </ng-container>

        <!-- Who -->
        <ng-container matColumnDef="user">
          <th mat-header-cell *matHeaderCellDef>Who</th>
          <td mat-cell *matCellDef="let row">
            <div class="who" [class.who--svc]="isServiceAccount(row.user)">
              <span class="who-ini">{{ initials(row.user) }}</span>
              <span class="who-name">{{ row.user }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Type -->
        <ng-container matColumnDef="entityType">
          <th mat-header-cell *matHeaderCellDef>Type</th>
          <td mat-cell *matCellDef="let row" class="cell-type">{{ row.entityType | titlecase }}</td>
        </ng-container>

        <!-- Record -->
        <ng-container matColumnDef="entityLabel">
          <th mat-header-cell *matHeaderCellDef>Record</th>
          <td mat-cell *matCellDef="let row" class="cell-record">
            <div class="rec-label">
              <ng-container *ngIf="splitRecordLabel(row.entityLabel) as parts; else plainLabel">
                <span class="rec-pre">{{ parts.prefix }}</span><span class="rec-id">{{ parts.id }}</span><span class="rec-post">{{ parts.suffix }}</span>
              </ng-container>
              <ng-template #plainLabel>{{ row.entityLabel }}</ng-template>
            </div>
            <div class="rec-uuid">{{ row.entityId }}</div>
          </td>
        </ng-container>

        <!-- Action chip -->
        <ng-container matColumnDef="action">
          <th mat-header-cell *matHeaderCellDef>Action</th>
          <td mat-cell *matCellDef="let row">
            <mat-chip-set>
              <mat-chip [ngClass]="chipClass(row.action)" disableRipple>
                {{ row.action | titlecase }}
              </mat-chip>
            </mat-chip-set>
          </td>
        </ng-container>

        <!-- Summary -->
        <ng-container matColumnDef="summary">
          <th mat-header-cell *matHeaderCellDef>Summary</th>
          <td mat-cell *matCellDef="let row" class="cell-summary">{{ row.summary }}</td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;"
            class="data-row"
            (click)="onRowClick(row)"
            [matTooltip]="'View timeline for ' + row.entityLabel"></tr>

      </table>
    </div>

    <mat-paginator
      [length]="totalCount"
      [pageSize]="pageSize"
      [pageSizeOptions]="[25, 50, 100, 200]"
      [pageIndex]="page"
      (page)="onPageChange($event)"
      showFirstLastButtons>
    </mat-paginator>
  </div>

</div>
